import type { AuthSession } from '@/features/_lib/auth-client.types'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import {
  Button,
  DialogClose,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/_components/ui'
import { Text, Title } from '@/features/_components/ui/typography'
import { ApiQueryError, mutations } from '@/features/_lib/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { CreateUserSchema } from '@/_schemas/create-user'
import { createUserSchema } from '@/_schemas/create-user'

type CreateUserDialogProps = {
  companyId: string
  session: AuthSession
  setIsDialogOpen: (isOpen: boolean) => void
}

export const CreateUserDialog = ({
  companyId,
  session: _session,
  setIsDialogOpen,
}: CreateUserDialogProps) => {
  const queryClient = useQueryClient()
  const inviteUser = useMutation(mutations.users.invite(queryClient))

  const form = useForm<CreateUserSchema>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      email: '',
    },
    shouldUnregister: true,
    disabled: inviteUser.isPending,
  })

  useEffect(() => {
    form.setValue('companyId', companyId)
  }, [companyId, form])

  const onSubmit = (data: CreateUserSchema) => {
    inviteUser.mutate(
      { email: data.email, companyId: data.companyId, role: 'member' },
      {
        onSuccess: () => {
          setIsDialogOpen(false)
          toast.success('Cadastro efetuado!', {
            description: 'Um e-mail foi enviado para definição de senha.',
          })
        },
        onError: (error) => {
          if (error instanceof ApiQueryError && error.apiError.status === 409) {
            form.setError('email', {
              type: 'manual',
              message: error.apiError.detail ?? 'Este e-mail já está em uso.',
            })
            return
          }
          toast.error('Algo deu errado. Tente novamente mais tarde.')
        },
      },
    )
  }

  return (
    <Form {...form}>
      <div className="flex flex-col items-center justify-center gap-1">
        <Title>Cadastro de usuário</Title>
        <Text>Insira as informações abaixo</Text>
      </div>
      <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>E-mail</FormLabel>
              <FormControl>
                <Input placeholder="Insira o e-mail" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex w-full justify-center gap-3">
          <DialogClose asChild>
            <Button
              variant="ghost"
              className="font-bold hover:bg-transparent hover:text-brand-grayscale-12"
              type="reset"
            >
              Cancelar
            </Button>
          </DialogClose>
          <Button type="submit" disabled={inviteUser.isPending} className="w-[142px] font-bold">
            {inviteUser.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Cadastrar
          </Button>
        </div>
      </form>
    </Form>
  )
}
